import * as v from "valibot";
import {
  AmenityRef,
  builderNameSchema,
  dateRangeSchema,
  destinationSchema,
  intRangeSchema,
  modelNameSchema,
  multiDictionarySchema,
  OperatorRef,
  paginationSchema,
  refListSchema,
  yachtNameSchema,
} from "./schemas";

export type FilterParams = {
  priceMin: number;
  priceMax: number;
  lengthMin: number;
  lengthMax: number;
  yearMin: number;
  yearMax: number;
  cabinsMin: number;
  cabinsMax: number;
  crewMin: number;
  crewMax: number;
  bathroomsMin: number;
  bathroomsMax: number;
  ratingMin: number;
  ratingMax: number;
};

export const searchParamsSchema = (p: FilterParams) =>
  v.object({
    period: v.optional(dateRangeSchema),
    pagination: paginationSchema([
      { label: "Sort by: Price/week ascending", value: "price_asc" },
      { label: "Sort by: Price/week descending", value: "price_desc" },
      { label: "Sort by: Discount", value: "discount" },
      { label: "Sort by: HELM recommended", value: "recommended" },
    ]),
    boatType: multiDictionarySchema([
      {
        value: "Sailing yacht",
        label: "Sailing yacht",
      },
      {
        value: "Catamaran",
        label: "Catamaran",
      },
      {
        value: "Do not display",
        label: "Do not display",
      },
      {
        value: "Gulet",
        label: "Gulet",
      },
      {
        value: "Jet ski",
        label: "Jet ski",
      },
      {
        value: "Power catamaran",
        label: "Power catamaran",
      },
      {
        value: "R.I.B / Speed boat",
        label: "R.I.B / Speed boat",
      },
      {
        value: "Other",
        label: "Other",
      },
      {
        value: "Motor yacht",
        label: "Motor yacht",
      },
    ]),
    destination: destinationSchema,
    operator: refListSchema(OperatorRef),
    operatorRating: intRangeSchema(1, 10),
    cabins: intRangeSchema(p.cabinsMin, p.cabinsMax),
    year: intRangeSchema(p.yearMin, p.yearMax),
    bathrooms: intRangeSchema(p.bathroomsMin, p.bathroomsMax),
    crewNumber: intRangeSchema(p.crewMin, p.crewMax),
    guests: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1), v.maxValue(20))),
    yachtName: yachtNameSchema,
    modeName: modelNameSchema,
    builderName: builderNameSchema,
    amenity: refListSchema(AmenityRef),
    mainsail: multiDictionarySchema([
      {
        label: "Classic",
        value: "classic",
      },
      {
        label: "Furling",
        value: "furling",
      },
    ]),
    length: intRangeSchema(p.lengthMin, p.lengthMax),
    charterType: multiDictionarySchema([
      {
        value: "bareboat",
        label: "Bareboat",
      },
      {
        value: "skippered",
        label: "Skippered",
      },
      {
        value: "crewed",
        label: "Crewed",
      },
    ]),
    provider: multiDictionarySchema([
      {
        label: "NauSYS",
        value: "NS",
      },
      {
        label: "MMK (Booking Manager)",
        value: "BM",
      },
      {
        label: "Central Yacht Agent",
        value: "CYA",
      },
      {
        label: "Yachtfolio",
        value: "Yachtfolio",
      },
      {
        label: "non-API yachts",
        value: "Helm",
      },
    ]),
    status: multiDictionarySchema([
      {
        label: "Delayed",
        value: "delayed",
      },
      {
        label: "Expired",
        value: "expired",
      },
    ]),
    price: intRangeSchema(p.priceMin, p.priceMax),
  });

export type SearchParams = v.InferOutput<ReturnType<typeof searchParamsSchema>>;

export function paramsToQuery(settings: FilterParams, sp: SearchParams) {
  let query = "mode=offers";

  const applyList = (name: string, values: string[]) => {
    if (values.length > 0) {
      for (const v of values) {
        query += `&${name}=${encodeURIComponent(v)}`;
      }
    }
  };
  const getDefaultRange = (defaultSetting: string) => ({
    defaultMin: settings[`${defaultSetting}Min` as keyof FilterParams],
    defaultMax: settings[`${defaultSetting}Max` as keyof FilterParams],
  });
  const applyRange = (
    name: string,
    value: { high: number; low: number },
    { defaultMax, defaultMin }: { defaultMin: number; defaultMax: number },
  ) => {
    if (value.high != defaultMax || value.low != defaultMin) {
      query += `&${name}_low=${value.low}&${name}_high=${value.high}`;
    }
  };

  if (sp.period) {
    query += `&date_from=${sp.period.dateFrom}&date_to=${sp.period.dateTo}`;
  }
  if (sp.pagination.order) {
    query += `&order=${sp.pagination.order}`;
  }
  if (sp.pagination.page) {
    query += `&page=${sp.pagination.page}`;
  }
  if (sp.pagination.pageSize) {
    query += `&page_size=${sp.pagination.pageSize}`;
  }

  for (const dst of sp.destination) {
    query += `&${dst.type}=${dst.ref}`;
  }

  if (sp.guests) {
    query += `&guests=${sp.guests}`;
  }

  applyList("boat_type", sp.boatType);
  applyList("operator", sp.operator);
  applyRange("operator_rating", sp.operatorRating, { defaultMin: 1, defaultMax: 10 });
  applyRange("cabins", sp.cabins, getDefaultRange("cabins"));
  applyRange("year", sp.year, getDefaultRange("year"));
  applyRange("bathrooms", sp.bathrooms, getDefaultRange("bathrooms"));
  applyRange("crew_number", sp.crewNumber, getDefaultRange("crew"));
  applyRange("length", sp.length, getDefaultRange("length"));
  applyRange("price", sp.price, getDefaultRange("price"));

  // applyList("yacht_name", [sp.yachtName]);
  // applyList("model_name", [sp.modeName]);
  // applyList("builder_name", [sp.builderName]);

  applyList("amenity", sp.amenity);
  applyList("mainsail", sp.mainsail);
  applyList("charter_type", sp.charterType);
  applyList("provider", sp.provider);
  applyList("status", sp.status);

  return query;
}
